/**
 * Keeps open pages in step with the admin's theme settings: every minute,
 * and whenever the tab becomes visible again, asks the server for the
 * current theme stylesheet digest and swaps in the new stylesheet if it
 * changed. The page does not reload.
 */

const CHECK_INTERVAL = 60_000;
const VERSION_PATH = '/site_themes/version';

export const THEME_CHANGED_EVENT = 'mastodon:site-themes-changed';

const currentLink = () =>
  document.getElementById('site-themes') as HTMLLinkElement | null;

// A new stylesheet is on its way: no other check starts until it has
// loaded or failed, so two of them are never added at once
let pending: Promise<void> | null = null;

/**
 * Replaces the stylesheet once the new one has loaded, without a flash. If
 * it cannot be loaded, the new element is removed and the old one stays.
 */
export const swapStylesheet = (link: HTMLLinkElement, digest: string) => {
  const next = link.cloneNode() as HTMLLinkElement;
  next.href = `/site_themes/${digest}.css`;
  next.dataset.digest = digest;
  // Keep the id on the element that is current
  next.removeAttribute('id');

  const done = new Promise<void>((resolve) => {
    next.addEventListener(
      'load',
      () => {
        link.remove();
        next.id = 'site-themes';
        window.dispatchEvent(new CustomEvent(THEME_CHANGED_EVENT));
        resolve();
      },
      { once: true },
    );

    next.addEventListener(
      'error',
      () => {
        next.remove();
        resolve();
      },
      { once: true },
    );
  });

  link.after(next);

  return done;
};

let checking = false;

export const checkForThemeChange = async () => {
  const link = currentLink();
  if (!link || checking || pending) return;

  checking = true;

  try {
    const response = await fetch(VERSION_PATH, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;

    const { digest } = (await response.json()) as { digest?: string };

    if (digest && digest !== link.dataset.digest) {
      pending = swapStylesheet(link, digest).finally(() => {
        pending = null;
      });
    }
  } catch {
    // Offline or the server is restarting; try again next time
  } finally {
    checking = false;
  }
};

export function setupThemeRefresh() {
  if (!currentLink()) return;

  window.setInterval(() => {
    if (document.visibilityState === 'visible') void checkForThemeChange();
  }, CHECK_INTERVAL);

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void checkForThemeChange();
  });
}
